import { translate } from '../lib/i18n'
import { useRef } from 'react'

export const CODE_LENGTH = 6

// Segmented one-time-code field: one box per digit, with auto-advance, backspace
// that steps back, arrow navigation and full-code paste. `value` stays a plain
// string of up to CODE_LENGTH digits so the parent owns the state.
export function CodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  className,
}: {
  value: string
  onChange: (next: string) => void
  onComplete: (code: string) => void
  disabled?: boolean
  className?: string
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const chars = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] ?? '')

  function commit(next: string[]) {
    const joined = next.join('')
    onChange(joined)
    if (joined.length === CODE_LENGTH) onComplete(joined)
  }

  function focusBox(index: number) {
    refs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))]?.focus()
  }

  function handleChange(index: number, raw: string) {
    const digits = raw.replace(/\D/g, '')
    if (!digits) return
    const next = [...chars]
    let cursor = index
    for (const digit of digits) {
      if (cursor >= CODE_LENGTH) break
      next[cursor] = digit
      cursor += 1
    }
    commit(next)
    focusBox(cursor)
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const next = [...chars]
      if (next[index]) {
        next[index] = ''
        commit(next)
      } else if (index > 0) {
        next[index - 1] = ''
        commit(next)
        focusBox(index - 1)
      }
    } else if (event.key === 'ArrowLeft') {
      focusBox(index - 1)
    } else if (event.key === 'ArrowRight') {
      focusBox(index + 1)
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault()
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!digits) return
    commit(Array.from({ length: CODE_LENGTH }, (_, i) => digits[i] ?? ''))
    focusBox(digits.length)
  }

  return (
    <div className={className ? `login-code-boxes ${className}` : 'login-code-boxes'}>
      {chars.map((char, index) => (
        <input
          key={index}
          ref={(el) => { refs.current[index] = el }}
          className="login-code-box"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={translate('code.digitAria', { n: index + 1 })}
          maxLength={1}
          value={char}
          disabled={disabled}
          autoFocus={index === 0}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}
